import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MyReview } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { myReviewView } from '../i18n/reviewLabels';
import { useMyReviews, type RateTarget } from '../hooks/useReviews';
import { canStillEdit, myReviewHeight } from '../hooks/reviewRules';
import { MyReviewCard, PendingRateRow } from '../components/Reviews';
import { VirtualList } from '../components/VirtualList';
import {
  Button,
  Card,
  EmptyState,
  ListSkeleton,
  Notice,
  PageHeader,
  SectionTitle,
} from '../components/ui';
import { orderTime } from '../utils/format';
import { RateFlow } from './RateFlow';

/**
 * Account › My reviews: orders waiting for a rating (Rate opens the sheet), then every review
 * I wrote — status (on hold / text hidden / removed), the restaurant's reply, Edit while the
 * edit window is open. The list windows itself past 100 reviews.
 */
export function MyReviewsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const mine = useMyReviews(true);
  const [target, setTarget] = useState<RateTarget | null>(null);
  // Edit windows are hours long: the time the page opened is precise enough.
  const [now] = useState(() => Date.now());

  const header = (
    <PageHeader title={t('my.title')} onBack={() => navigate(-1)} backLabel={t('common.back')} />
  );

  return (
    <div className="min-h-dvh bg-page pb-10">
      {header}
      <div className="flex flex-col gap-5 px-4 pt-4">
        {mine.loading ? (
          <ListSkeleton rows={4} />
        ) : mine.failed ? (
          <Notice
            tone="negative"
            supportRef
            title={t('common.wrong')}
            action={
              <Button size="sm" tone="outline" onClick={mine.retry}>
                {t('common.retry')}
              </Button>
            }
          />
        ) : (
          <>
            {mine.pending.length > 0 && (
              <section>
                <SectionTitle>{t('my.toRate')}</SectionTitle>
                <Card flush className="divide-y divide-hair">
                  {mine.pending.map((item) => (
                    <PendingRateRow
                      key={item.orderId}
                      outletName={item.outletName}
                      meta={`${t('my.order', { number: item.orderNo })} · ${t('rate.rateBy', { time: orderTime(item.rateBy) })}`}
                      actionLabel={t('rate.stripAction')}
                      onRate={() => setTarget({ kind: 'new', pending: item })}
                    />
                  ))}
                </Card>
              </section>
            )}
            <section>
              {mine.reviews.length > 0 && <SectionTitle>{t('my.yours')}</SectionTitle>}
              {mine.reviews.length === 0 ? (
                mine.pending.length === 0 && (
                  <EmptyState icon="star" title={t('my.empty')}>
                    {t('my.emptyBody')}
                  </EmptyState>
                )
              ) : (
                <VirtualList<MyReview>
                  items={mine.reviews}
                  rowHeight={(review) => myReviewHeight(review, canStillEdit(review, now))}
                  getKey={(review) => review.id}
                  renderRow={(review) => (
                    <MyReviewCard
                      review={myReviewView(t, review, now)}
                      onEdit={() => setTarget({ kind: 'edit', review })}
                      onOpen={() => navigate(`/orders/${review.orderId}`)}
                    />
                  )}
                />
              )}
            </section>
          </>
        )}
      </div>
      {target && (
        <RateFlow
          key={target.kind === 'edit' ? target.review.id : target.pending.orderId}
          target={target}
          onClose={() => setTarget(null)}
        />
      )}
    </div>
  );
}
